// src/pages/public/ContactPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  MapPin, Phone, Mail, Clock, MessageCircle,
  Send, Building2, Users, Award, TrendingUp,
  ChevronDown, ArrowRight, Shield,
} from 'lucide-react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import MainLayout from '../../components/layout/MainLayout';
/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
  green500:   '#22c55e',
  green600:   '#16a34a',
};

/* ── useInView ── */
const useInView = (threshold = 0.12) => {
  const ref       = useRef(null);
  const [inView, setInView] = useState(false);
  const triggered = useRef(false);
  useEffect(() => {
    if (triggered.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        setInView(true);
        obs.disconnect();
      }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = {
    up:    'translateY(50px)',
    left:  'translateX(-50px)',
    right: 'translateX(50px)',
    scale: 'scale(0.88)',
  };
  return (
    <div ref={ref} style={{
      opacity:    inView ? 1 : 0,
      transform:  inView ? 'none' : from[dir],
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms,
                   transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── Floating Orb ── */
const Orb = ({ size, color, top, left, delay = 0 }) => (
  <div style={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, top, left, filter: 'blur(70px)', opacity: 0.28,
    animation: `floatOrb ${7 + delay}s ease-in-out infinite alternate`,
    animationDelay: `${delay}s`, pointerEvents: 'none', zIndex: 0,
  }} />
);

/* ── SectionLabel ── */
const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 18 }}>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
    <span style={{ fontSize: 12, color: C.amber, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, #f59e0b, ${C.amber})`, borderRadius: 2 }} />
  </div>
);

/* ── CountUp ── */
const CountUp = ({ end, suffix = '' }) => {
  const [n, setN]     = useState(0);
  const [ref, inView] = useInView();
  const done          = useRef(false);
  useEffect(() => {
    if (!inView || done.current) return;
    done.current = true;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - t0) / 2000, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView]);
  return <span ref={ref}>{n}{suffix}</span>;
};

/* ── Input Field ── */
const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: C.gray700, marginBottom: 8 }}>
      {label} {required && <span style={{ color: C.amber }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '13px 16px', borderRadius: 12, fontFamily: 'inherit',
  border: `1.5px solid ${C.gray200}`, fontSize: 14.5, color: C.gray900,
  background: C.white, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
};

/* ── ContactCard ── */
const ContactCard = ({ Icon, iconColor, iconBg, title, description, content, subcontent, href, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 18,
          background: C.white, borderRadius: 18, padding: '22px 24px',
          border: `1.5px solid ${hov ? C.amber : C.gray100}`,
          boxShadow: hov ? '0 20px 48px rgba(0,0,0,0.1)' : '0 4px 16px rgba(0,0,0,0.06)',
          transform: hov ? 'translateY(-5px)' : 'translateY(0)',
          transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: '50%', backgroundColor: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          transform: hov ? 'scale(1.15) rotate(-8deg)' : 'scale(1)',
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          <Icon size={22} color={iconColor} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: C.gray900, fontSize: 15.5, marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 13, color: C.gray500, marginBottom: 8 }}>{description}</div>
          {href ? (
            <a href={href} target={href.startsWith('http') ? '_blank' : undefined}
              rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
              style={{ color: C.amber, fontWeight: 700, fontSize: 14.5, textDecoration: 'none' }}
            >
              {content}
              {subcontent && <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{subcontent}</span>}
            </a>
          ) : (
            <div style={{ color: C.gray700, fontWeight: 600, fontSize: 14.5 }}>
              {content}
              {subcontent && <div style={{ fontSize: 13 }}>{subcontent}</div>}
            </div>
          )}
        </div>
      </div>
    </Anim>
  );
};

/* ── FAQCard ── */
const FAQCard = ({ question, answer, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: hov ? 'linear-gradient(135deg, #fffbeb, #fff7ed)' : C.gray50,
          borderRadius: 18, padding: '28px 30px',
          border: `1.5px solid ${hov ? 'rgba(217,119,6,0.22)' : 'transparent'}`,
          boxShadow: hov ? '0 16px 40px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.04)',
          transform: hov ? 'translateY(-4px)' : 'translateY(0)',
          transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: C.amber }}>Q</span>
          </div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900 }}>{question}</h3>
        </div>
        <p style={{ fontSize: 14.5, color: C.gray600, lineHeight: 1.78, paddingLeft: 42 }}>{answer}</p>
      </div>
    </Anim>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const ContactPage = () => {
  const videoRef = useRef(null);
  const [scrollY, setScrollY]   = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [formData, setFormData] = useState({
    name: '', phone: '', propertyType: '', message: '',
  });
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onMouse  = e  => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('mousemove', onMouse); };
  }, []);

  const heroOpacity  = Math.max(0, 1 - scrollY / 500);
  const heroParallax = scrollY * 0.35;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    const message = `Hello Dewanta Property!\n\n*Name:* ${formData.name}\n*Phone:* ${formData.phone}\n*Property Type:* ${formData.propertyType}\n*Message:* ${formData.message}\n\nI'm interested in learning more about your villa management services.`;
    window.open(`https://wa.me/6287785686777?text=${encodeURIComponent(message)}`, '_blank');
    setFormData({ name: '', phone: '', propertyType: '', message: '' });
  };

  const contactMethods = [
    { Icon: Phone,         iconColor: C.amber,    iconBg: C.amberLight, title: 'Phone',           description: 'Call us anytime during business hours', content: '+62 877-8568-6777',                   href: 'tel:+6287785686777' },
    { Icon: MessageCircle, iconColor: '#16a34a',  iconBg: '#dcfce7',    title: 'WhatsApp',         description: 'Quick response via WhatsApp',           content: '+62 877-8568-6777',                   href: 'https://wa.me/6287785686777' },
    { Icon: Mail,          iconColor: '#2563eb',  iconBg: '#dbeafe',    title: 'Email',            description: 'Send us detailed inquiries',             content: 'dewantavillas.management@gmail.com',  href: 'mailto:dewantavillas.management@gmail.com' },
    { Icon: MapPin,        iconColor: '#7c3aed',  iconBg: '#ede9fe',    title: 'Office Location',  description: 'Visit us at our office',                content: 'Jl Bypass Darma Giri Buruan',          subcontent: 'Blahbatuh Gianyar, Bali' },
  ];

  const faqs = [
    { question: 'What services do you offer?',         answer: 'We provide comprehensive villa management services including property maintenance, guest services, marketing across 15+ booking platforms, 24/7 support, and revenue optimization.' },
    { question: 'How does the management fee work?',   answer: 'We offer flexible fee structures based on either a percentage of revenue or fixed monthly fees, depending on the package you choose. All transactions are transparent and well-documented.' },
    { question: 'Is there a minimum contract period?', answer: "The minimum contract is 6 months, which provides adequate time for marketing optimization and property management setup to maximize your villa's potential." },
    { question: 'How do I start working with Dewanta?', answer: 'Simply contact us via WhatsApp or phone for a free consultation. Our team will conduct a property survey and provide a customized proposal tailored to your needs.' },
  ];

  return (
    <MainLayout>
    <div style={{ minHeight: '100vh', backgroundColor: C.white, overflowX: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes floatOrb  { 0% { transform: translateY(0) scale(1); } 100% { transform: translateY(-40px) scale(1.1); } }
        @keyframes heroIn    { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer   { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes pulseDot  { 0%,100% { box-shadow: 0 0 0 0 rgba(217,119,6,0.6); } 50% { box-shadow: 0 0 0 10px rgba(217,119,6,0); } }
        @keyframes bounce    { 0%,100% { transform: translateY(0); opacity: 1; } 50% { transform: translateY(10px); opacity: 0.4; } }

        .ch-1 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.20s both; }
        .ch-2 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
        .ch-3 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.70s both; }

        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        .scroll-bounce { animation: bounce 2s ease-in-out infinite; }

        .contact-input:focus {
          border-color: #d97706 !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }
        .contact-select:focus {
          border-color: #d97706 !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }
        .contact-textarea:focus {
          border-color: #d97706 !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }

        .cursor-glow {
          pointer-events: none; position: fixed; width: 280px; height: 280px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,119,6,0.06) 0%, transparent 70%);
          transform: translate(-50%,-50%);
          transition: left 0.12s ease, top 0.12s ease;
          z-index: 9999; mix-blend-mode: multiply;
        }

        .stat-item { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); cursor: default; }
        .stat-item:hover { transform: translateY(-8px); }

        .hours-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; color: #374151; }
        .hours-row:not(:last-child) { border-bottom: 1px solid #fde68a; }
      `}</style>

      {/* Cursor glow */}
      <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />

      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', height: '60vh', minHeight: 480, overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted loop playsInline style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: `translateY(${heroParallax}px)`, willChange: 'transform',
        }}>
          <source src="/images/hero-video.mp4" type="video/mp4" />
          <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1920&q=80" alt="Contact" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </video>

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.55), rgba(0,0,0,0.72))' }} />
        <Orb size="360px" color="radial-gradient(circle, #d97706, #f59e0b)" top="8%"  left="66%" delay={0} />
        <Orb size="220px" color="radial-gradient(circle, #92400e, #d97706)" top="55%" left="-3%" delay={2} />

        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 24px',
          transform: `translateY(${heroParallax * 0.4}px)`, opacity: heroOpacity,
        }}>
          <div className="ch-1" style={{ marginBottom: 20 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 22px', backgroundColor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 50, color: '#fff', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.amber, animation: 'pulseDot 2s ease-in-out infinite' }} />
              Free Consultation Available
            </span>
          </div>

          <h1 className="ch-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(34px,7vw,72px)', fontWeight: 700,
            color: '#fff', lineHeight: 1.08, letterSpacing: '-0.02em', marginBottom: 16,
          }}>
            Let's Talk About<br /><span className="shimmer-text">Your Villa</span>
          </h1>

          <p className="ch-3" style={{ fontSize: 'clamp(15px,2vw,19px)', color: 'rgba(255,255,255,0.82)', maxWidth: 560, fontWeight: 300, lineHeight: 1.72 }}>
            Ready to maximize your property's potential? Our team is here to help you succeed.
          </p>
        </div>

        <div className="scroll-bounce" style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.55)' }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* ══ STATS STRIP ══ */}
      <section style={{ padding: '52px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="220px" color="radial-gradient(circle, rgba(217,119,6,0.32), transparent)" top="-50%" left="28%" />
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 32, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {[
            { Icon: Building2,  end: 50,   suffix: '+',  label: 'Managed Properties' },
            { Icon: Users,      end: 1000, suffix: '+',  label: 'Happy Guests'       },
            { Icon: Award,      end: 5,    suffix: '.0', label: 'Average Rating'     },
            { Icon: TrendingUp, end: 95,   suffix: '%',  label: 'Occupancy Rate'     },
          ].map((s, i) => (
            <Anim key={i} delay={i * 100}>
              <div className="stat-item">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                  <s.Icon size={28} color={C.amber} />
                </div>
                <div className="shimmer-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 48, fontWeight: 700, marginBottom: 4 }}>
                  <CountUp end={s.end} suffix={s.suffix} />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.label}</div>
              </div>
            </Anim>
          ))}
        </div>
      </section>

      {/* ══ MAIN CONTACT SECTION ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="460px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="-5%" left="-6%" delay={3} />
        <Orb size="360px" color="radial-gradient(circle, rgba(217,119,6,0.06), transparent)" top="50%" left="72%" delay={1} />

        <div style={{ maxWidth: 1160, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px,1fr))', gap: 64, position: 'relative', zIndex: 1 }}>

          {/* ── LEFT: Form ── */}
          <div>
            <Anim dir="left" delay={0}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 40, height: 3, backgroundColor: C.amber, borderRadius: 2 }} />
                <span style={{ padding: '6px 16px', backgroundColor: C.amberLight, color: C.amberDark, borderRadius: 50, fontSize: 13, fontWeight: 700 }}>Get In Touch</span>
              </div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,48px)', fontWeight: 700, color: C.gray900, lineHeight: 1.12, marginBottom: 14 }}>
                Send Us a<br /><span style={{ color: C.amber }}>Message</span>
              </h2>
              <p style={{ fontSize: 15.5, color: C.gray600, lineHeight: 1.78, marginBottom: 34 }}>
                Fill out the form below and we'll connect with you instantly via WhatsApp — the fastest way to start your villa management journey.
              </p>
            </Anim>

            <Anim dir="left" delay={100}>
              <div style={{
                background: C.gray50, borderRadius: 24, padding: '36px',
                border: `1.5px solid ${C.gray100}`, boxShadow: '0 8px 32px rgba(0,0,0,0.07)',
              }}>
                {/* Form header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${C.gray200}` }}>
                  <div style={{ width: 52, height: 52, backgroundColor: '#16a34a', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MessageCircle size={24} color="#fff" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: C.gray900, fontSize: 16.5 }}>WhatsApp Direct Contact</div>
                    <div style={{ fontSize: 13, color: C.gray500 }}>Instant response guaranteed</div>
                  </div>
                </div>

                <form onSubmit={handleWhatsAppSubmit}>
                  <Field label="Your Name" required>
                    <input
                      className="contact-input"
                      type="text" name="name" value={formData.name}
                      onChange={handleInputChange} required
                      placeholder="Enter your full name"
                      style={{ ...inputStyle }}
                    />
                  </Field>

                  <Field label="Phone Number" required>
                    <input
                      className="contact-input"
                      type="tel" name="phone" value={formData.phone}
                      onChange={handleInputChange} required
                      placeholder="+62 xxx xxxx xxxx"
                      style={{ ...inputStyle }}
                    />
                  </Field>

                  <Field label="Property Type" required>
                    <select
                      className="contact-select"
                      name="propertyType" value={formData.propertyType}
                      onChange={handleInputChange} required
                      style={{ ...inputStyle, cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
                    >
                      <option value="">Select property type</option>
                      <option value="1-2 Bedroom Villa">1-2 Bedroom Villa</option>
                      <option value="3-4 Bedroom Villa">3-4 Bedroom Villa</option>
                      <option value="5+ Bedroom Villa">5+ Bedroom Villa</option>
                      <option value="Apartment/Studio">Apartment/Studio</option>
                      <option value="Guest House">Guest House</option>
                      <option value="Other">Other</option>
                    </select>
                  </Field>

                  <Field label="Your Message">
                    <textarea
                      className="contact-textarea"
                      name="message" value={formData.message}
                      onChange={handleInputChange} rows={4}
                      placeholder="Tell us about your property or any questions you have..."
                      style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }}
                    />
                  </Field>

                  <SubmitBtn />
                </form>

                <p style={{ fontSize: 12.5, color: C.gray400, textAlign: 'center', marginTop: 16, lineHeight: 1.6 }}>
                  By submitting, you'll be redirected to WhatsApp where you can continue the conversation with our team
                </p>
              </div>
            </Anim>
          </div>

          {/* ── RIGHT: Contact info ── */}
          <div>
            <Anim dir="right" delay={0}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 40, height: 3, backgroundColor: C.amber, borderRadius: 2 }} />
                <span style={{ padding: '6px 16px', backgroundColor: C.amberLight, color: C.amberDark, borderRadius: 50, fontSize: 13, fontWeight: 700 }}>Contact Info</span>
              </div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,48px)', fontWeight: 700, color: C.gray900, lineHeight: 1.12, marginBottom: 14 }}>
                Multiple Ways<br /><span style={{ color: C.amber }}>to Reach Us</span>
              </h2>
              <p style={{ fontSize: 15.5, color: C.gray600, lineHeight: 1.78, marginBottom: 34 }}>
                Choose the channel that works best for you. We're always ready to help.
              </p>
            </Anim>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
              {contactMethods.map((m, i) => (
                <ContactCard key={i} delay={i * 80} {...m} />
              ))}
            </div>

            {/* Business Hours */}
            <Anim dir="right" delay={360}>
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb, #fff7ed)',
                borderRadius: 20, padding: '26px 28px',
                border: '1.5px solid rgba(217,119,6,0.18)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock size={20} color={C.amber} />
                  </div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.gray900 }}>Business Hours</h3>
                </div>
                <div>
                  {[
                    { day: 'Monday – Friday', hours: '8:00 AM – 8:00 PM' },
                    { day: 'Saturday',        hours: '9:00 AM – 6:00 PM' },
                    { day: 'Sunday',          hours: '10:00 AM – 4:00 PM' },
                  ].map((row, i) => (
                    <div key={i} className="hours-row">
                      <span style={{ fontWeight: 600, color: C.gray700 }}>{row.day}</span>
                      <span style={{ color: C.gray600 }}>{row.hours}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#16a34a', animation: 'pulseDot 2s ease-in-out infinite' }} />
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: '#16a34a' }}>24/7 Emergency Support Available</span>
                  </div>
                </div>
              </div>
            </Anim>
          </div>
        </div>
      </section>

      {/* ══ MAP ══ */}
      <section style={{ padding: '80px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="320px" color="radial-gradient(circle, rgba(217,119,6,0.07), transparent)" top="0%" left="70%" delay={2} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <SectionLabel text="Our Location" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,46px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Find Our Office
              </h2>
              <p style={{ fontSize: 15.5, color: C.gray500 }}>
                Jl Bypass Darma Giri Buruan, Blahbatuh Gianyar, Bali, Indonesia
              </p>
            </div>
          </Anim>
          <Anim delay={100}>
            <div style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.14)' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126214.40380955465!2d115.18881532475142!3d-8.670458044482498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd2409b0e5e80db%3A0xe27334e8ccb9b00!2sGianyar%2C%20Bali!5e0!3m2!1sen!2sid!4v1647929837195!5m2!1sen!2sid"
                width="100%" height="440"
                style={{ border: 0, display: 'block' }}
                allowFullScreen="" loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              />
            </div>
          </Anim>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="400px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="0%" left="-5%" delay={4} />
        <div style={{ maxWidth: 860, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <SectionLabel text="FAQ" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                Frequently Asked Questions
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500 }}>
                Quick answers to common questions about our services
              </p>
            </div>
          </Anim>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {faqs.map((faq, i) => (
              <FAQCard key={i} delay={i * 80} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(217,119,6,0.22), transparent)" top="0%"  left="46%" delay={0} />
        <Orb size="300px" color="radial-gradient(circle, rgba(251,191,36,0.12), transparent)" top="55%" left="-3%" delay={3} />
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <p style={{ color: C.amber, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 20 }}>Ready to Start?</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(32px,6vw,60px)', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 18 }}>
              Ready to Maximize Your<br /><span className="shimmer-text">Villa's Potential?</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 42, lineHeight: 1.78 }}>
              Join 50+ property owners who trust us with their villas. Let's discuss how we can help you achieve better results.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <CTABtn
                href="https://wa.me/6287785686777"
                bg={C.white} color={C.amber} hoverBg="#f3f4f6"
                icon={<MessageCircle size={18} />}
              >
                Chat on WhatsApp
              </CTABtn>
              <CTABtn
                href="tel:+6287785686777"
                bg="transparent" color={C.white} hoverBg="rgba(255,255,255,0.1)"
                border="2px solid rgba(255,255,255,0.45)"
                icon={<Phone size={18} />}
              >
                Call Us Now
              </CTABtn>
            </div>
          </Anim>
        </div>
      </section>


    </div>
    </MainLayout>
  );
};

/* ── SubmitBtn — terpisah untuk local hover state ── */
const SubmitBtn = () => {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="submit"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', padding: '16px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? '#15803d' : '#16a34a',
        color: '#fff', fontWeight: 700, fontSize: 15.5, fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 14px 32px rgba(22,163,74,0.42)' : '0 4px 16px rgba(22,163,74,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <MessageCircle size={19} />
      Continue on WhatsApp
      <Send size={15} />
    </button>
  );
};

/* ── CTABtn ── */
const CTABtn = ({ href, bg, color, hoverBg, border, icon, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '15px 32px', borderRadius: 50, fontFamily: 'inherit',
        backgroundColor: hov ? hoverBg : bg, color,
        border: border || 'none', textDecoration: 'none',
        fontWeight: 700, fontSize: 15,
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 12px 28px rgba(0,0,0,0.18)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      {icon} {children}
    </a>

  );
};

export default ContactPage;